import { chunkWindow, deriveFromDisplay, DisplayDerivation, LinerWindow, RawTemperatureResponse, ZoneDerivation } from './zone-derivation';
import { ZoneFoldRefused } from './zone-fold';
import { Injectable } from '@angular/core';
import { medianGapMs } from './median-gap';
import { SUPPORT_EMAIL, SUPPORT_PHONE } from '../support-contact';
import { serverReasonOf } from '../http-failure';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  Observable,
  throwError,
  of,
  Subject,
  EMPTY,
  timer,
  race,
  firstValueFrom,
  from,
} from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  map,
  retryWhen,
  tap,
  finalize,
  shareReplay,
  filter,
  take,
  switchMap,
  last,
} from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { TemperatureDataRow } from '../models/temperature-data-row';
import { ZoneDataRow } from '../models/zone-data-row';
import { environment } from './../../environments/environment';
import { ProjectTemperatureData } from '../models/project-temperature-data';
import { ProjectZoneData } from '../models/project-zone-data';
import { ProjectLossData } from '../models/project-loss-data';
import { EpochRange, isBounded } from './project-lifetime';

/**
 * Whatever one family's backfill returns.
 *
 * Named so a consumer can hold "a historical payload" without caring which
 * family produced it — the shape a stats or server-side report path needs, and
 * the reason ensureHistorical returns rather than only publishing.
 */
export type HistoricalPayload =
  | ProjectTemperatureData
  | ProjectZoneData
  | ProjectLossData;
import { MarkerInfo } from '../models/marker-info';
import { Note } from '../models/note';
import { ProjectInfo, ProjectInfoResponse } from '../models/project-info';
import { VericureUser } from '../models/vericure-user';
import { UserAuthService } from '../UserAuth.service';
import { checkWindowStart, reportWindowStart } from './window-integrity';
import { LANGUAGE_ENDONYM } from '../i18n/languages';
import { PreferencesService } from './preferences.service';
import {
  TelemetryCursorSet,
  TelemetryFamily,
  maxEpochOf,
} from './telemetry-cursor';
import { DownsampleParams } from './telemetry-downsample';
import { HistoricalCacheService } from './historical-cache.service';
import { AdaptiveResolutionService } from './adaptive-resolution.service';
import { BYTES_PER_POINT_ESTIMATE } from './adaptive-resolution';
import { isAuthFailure, isRecoveryExhausted } from '../auth/auth-failure';
import { projectListSignature, isUnchangedList } from './project-list-signature';
import {
  AUTH_RETRY_ATTEMPTS,
  AuthorizationResult,
  authRetryDelayMs,
  classifyAuthorizationError,
} from '../auth/authorization-result';

export type DeviceStatus = 'online' | 'offline' | 'unknown';

// A DISPLAY LABEL, so it is the endonym map — not LANGUAGE_FILE, which names
// dictionary FILES. This aliased the filename map for a while because their
// values coincide in English; they are different facts and the desktop already
// disagrees with itself about one of them (Spanish: LanguageID "SP", ISO "es").
const LANG_CODE_TO_LABEL = LANGUAGE_ENDONYM;

/**
 * The outcome of the last project-list load.
 *
 * `loaded` with count 0 and `failed` are DIFFERENT THINGS and used to render
 * identically. That is the whole reason this type exists.
 */
export type ProjectListLoad =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'loaded'; count: number }
  | {
      kind: 'failed';
      status: number;
      message: string;
      detail: string;
      /** Identity problem, not a network one — retrying cannot fix it. */
      authRequired?: boolean;
      /** Automatic re-sign-in already spent its budget; offer a manual button. */
      recoveryExhausted?: boolean;
    };

/**
 * The strip's wording for a failed raw=1 request (contract clarifications,
 * 2026-09-24). The server's two raw error codes are named; anything else is a
 * server error with its status. It is never "no data".
 */
export function rawFailureReason(err: { status?: number; error?: unknown } | null | undefined): string {
  let body: unknown = err?.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      body = null;
    }
  }
  const code = (body as { error?: unknown } | null)?.error;
  if (code === 'raw_read_failed') {
    return 'server error (raw_read_failed).';
  }
  if (code === 'raw_window_invalid') {
    return 'the server refused the liner window (raw_window_invalid).';
  }
  const status = typeof err?.status === 'number' ? err.status : 0;
  return status > 0 ? `server error (HTTP ${status}).` : 'the request did not reach the server.';
}

/**
 * Why GetProjectlist must NOT be sent for these roles and this company, or null.
 *
 * DataServices (ff2f917) answers 403 company_selection_required when SuperAdmin
 * or administrator asks with no customerId. Before that fix, the same request
 * came back unscoped. Neither is a list this user should see, so the client
 * does not ask. Unknown roles (empty) are refused too: without them there is no
 * way to tell whether a company is required, and guessing "not required" is the
 * race this closes.
 */
export function projectListBlockReason(
  roles: readonly string[],
  activeCustomerId: string
): 'roles_unknown' | 'company_selection_required' | null {
  if (!roles || roles.length === 0) {
    return 'roles_unknown';
  }
  const needsCompany = roles.includes('SuperAdmin') || roles.includes('administrator');
  return needsCompany && !activeCustomerId ? 'company_selection_required' : null;
}

/**
 * Turn a failed load into something a person can act on.
 *
 * The server is usually explicit — the 404 that hid Joel's projects carried
 * `{"error":"No devices found for customer"}`, which names the fault precisely.
 * That body was being thrown away. The server's own words are passed through as
 * `detail` rather than being re-interpreted here: this layer does not know what
 * every server message means, and inventing a friendlier paraphrase is how a
 * diagnosis becomes a guess.
 *
 * status 0 gets the same treatment as the uploader: no response reached us, so
 * do not blame the user's connection and do not claim to know why.
 */
export function describeProjectListFailure(
  error?: { status?: number; error?: unknown; message?: string },
  overrideMessage?: string
): Extract<ProjectListLoad, { kind: 'failed' }> {
  const status = typeof error?.status === 'number' ? error.status : 0;

  const serverText = serverReasonOf(error);

  // AN EXPIRED SESSION IS NOT A NETWORK PROBLEM. The interceptor refuses a
  // token-less request with status 0 because no HTTP call happened, and this
  // function used to read that as "no response came back" and tell the user to
  // try again. Retrying an expired token fails identically, forever.
  if (isAuthFailure(error)) {
    const exhausted = isRecoveryExhausted(error);
    return {
      kind: 'failed',
      status,
      authRequired: true,
      recoveryExhausted: exhausted,
      message: exhausted
        ? 'Your session has expired and signing in again did not restore it. ' +
          `Sign in to continue, or contact Vericure Support at ${SUPPORT_EMAIL} or ${SUPPORT_PHONE}.`
        : 'Your session has expired. Taking you to sign in — you will come back to this page.',
      detail: serverText,
    };
  }

  const message =
    overrideMessage ??
    (status === 0
      ? 'Could not load your projects — no response came back. This is usually a ' +
        'server or configuration problem rather than a fault on your side, though ' +
        'a dropped connection can look the same.'
      : 'Could not load your projects. This is not the same as having none — the ' +
        'list could not be retrieved.');

  const parts = [serverText, status ? `HTTP ${status}` : 'no response'].filter(Boolean);
  return {
    kind: 'failed',
    status,
    message: `${message} If it keeps happening, contact Vericure Support at ${SUPPORT_EMAIL} or ${SUPPORT_PHONE}.`,
    detail: parts.join(' · '),
  };
}

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  /** DIAGNOSTIC (temporary): how many realtime fetches this page has issued. */
  static rtPollCount = 0;
  /** DIAGNOSTIC (temporary): how many were paced away. */
  static rtPollSuppressed = 0;

  /**
   * Fallback gap when the measurement interval is not known yet. Deliberately
   * conservative: the first poll of a project happens before any payload has
   * reported MI, and guessing fast would reintroduce the very hammering this
   * exists to prevent.
   */
  static readonly DEFAULT_RT_FETCH_MS = 10_000;

  /** Floor, so a nonsensical MI cannot turn into a hot loop. */
  static readonly MIN_RT_FETCH_MS = 1000;

  /**
   * Floor for the offline threshold. A rig reporting a tiny MI would otherwise
   * declare itself offline between two consecutive readings.
   */
  static readonly MIN_OFFLINE_THRESHOLD_MS = 30000;

  /**
   * Arrival times of recent data, newest last. The OBSERVED cadence.
   *
   * MI IS NOT TRUSTWORTHY. Measured on real projects: MI says 5 s while the
   * actual median spacing is 14.5 s, ranging 8.2 s to 116.1 s. A threshold of
   * 2.5 x MI would fire at ~12.5 s against data that legitimately arrives 14.5 s
   * apart — the pill would read "Offline" on a healthy streaming project. Wrong
   * by 3x is worse than absent, because absent at least fails obviously.
   */
  private arrivalTimes: number[] = [];
  /** Enough intervals to have an opinion. Below this we decline to judge. */
  /**
   * Longest anything will wait for the unit preference before proceeding.
   *
   * RAISED FROM 4 s, WHICH WAS THE WRONG TRADE. I chose 4 s reasoning that
   * expiry "degrades to today's behaviour", and that was wrong on the facts:
   * what it degrades to is the DEFAULT_EFFECTIVE_PREFERENCES metric placeholder
   * — Celsius and metres — for an account whose stored preference is imperial.
   * That is not a slower paint, it is the wrong answer to the exact thing
   * promised to the customer, delivered silently and self-consistently so
   * nothing on screen looks wrong.
   *
   * MEASURED on prod2, warm Function App: the v1/preferences GET took 743 ms but
   * did not START until 2171 ms into the load, because it waits on MSAL. So a
   * warm client already spends most of a 4 s budget, and a COLD DataServices
   * Function App — which is precisely a first sign-in on a fresh machine, i.e. a
   * demo — routinely takes 5-15 s to answer. The bound was expiring on the one
   * client that most needed it to hold.
   *
   * WHY NOT UNBOUNDED: both this gate and whenUnitsSettled() sit in front of
   * chart construction, so no bound at all means a permanently blank dashboard
   * if preferences never resolve. 20 s clears a cold start with room, and the
   * loading overlay already covers the wait. A returning user pays NOTHING —
   * load() hydrates the cache first, so settled$ is already true and both paths
   * are synchronous.
   */
  private static readonly UNITS_SETTLE_TIMEOUT_MS = 20000;

  static readonly MIN_ARRIVALS_FOR_CADENCE = 4;
  private static readonly ARRIVAL_WINDOW = 12;

  /** The project's measurement interval, in SECONDS, as reported by the payload. */
  private measurementIntervalSec = 0;
  private lastRealtimeFetchAt = 0;

  /**
   * Minimum gap between realtime fetches: THE PROJECT'S OWN DATA CADENCE.
   *
   * Polling faster than the CMS produces readings cannot return anything new, so
   * the interval is read from MI rather than fixed. A rig on a 60 s cadence gets
   * polled every 60 s; one on 10 s gets 10 s. A fixed cap was arbitrary in both
   * directions — needless load on a slow rig, and needless latency on a fast one.
   */
  realtimeFetchGapMs(): number {
    const mi = this.measurementIntervalSec;
    if (!(mi > 0)) {
      return ProjectService.DEFAULT_RT_FETCH_MS;
    }
    return Math.max(ProjectService.MIN_RT_FETCH_MS, Math.round(mi * 1000));
  }

  /** Remember the cadence whenever a payload reports one. */
  private noteMeasurementInterval(mi: number | undefined): void {
    if (typeof mi === 'number' && mi > 0) {
      this.measurementIntervalSec = mi;
    }
  }
  projectList: ProjectInfo[] = [];

  /**
   * Fingerprint of the last project-list payload PUBLISHED to subscribers.
   *
   * Only ever read for quiet refetches, and null means "nothing published yet",
   * which is never treated as unchanged. See project-list-signature.ts.
   */
  private projectListSignature: string | null = null;

  /**
   * Whether the last project-list load SUCCEEDED — distinct from whether it
   * returned anything.
   *
   * These were the same thing until now: the error handler set projectList = []
   * and logged to a console nobody reads, so a 404 whose body literally said
   * "No devices found for customer" rendered as the ordinary empty state. A user
   * cannot tell "you have no projects" from "we could not find out", and the two
   * need completely different actions from them.
   */
  private projectListLoadSource = new BehaviorSubject<ProjectListLoad>({ kind: 'idle' });
  projectListLoad$ = this.projectListLoadSource.asObservable();

  get projectListLoad(): ProjectListLoad {
    return this.projectListLoadSource.getValue();
  }
  tenandId = 'faa4be5e-a16c-4943-b00e-a8edc85cf171';
  isMetricUnit: boolean = true;
  isHelpOverlayVisible: boolean = false;
  /**
   * Pauses the 5s realtime poll while PDF export renders charts offscreen.
   * Multiple chart components can be alive at once during export and each one
   * pushes RT rows into the shared envelope arrays — an RT emission mid-export
   * would double-append rows. Checked by the main-layout poll gate.
   */
  suspendRealtime: boolean = false;
  selectedLang: string = '';
  toUpload_isMetricUnit: boolean = true;
  toUpload_selectedLang: string = 'English';
  
  // Per-project unit preferences (in-memory storage)
  private projectUnitPreferences: Map<string, boolean> = new Map();
  private projectLanguagePreferences: Map<string, string> = new Map();
  
  // Marker positioning constants
  private readonly INITIAL_MARKER_POSITION = 10; // meters
  private readonly MINIMUM_MARKER_SPACING = 0.5; // meters
  private readonly MARKER_INCREMENT = 1; // meters
  private samplingInterval: number = 0.15; // Default value, will be updated from API

  private selectedProjectSource = new BehaviorSubject<string>('');
  selectedProject = this.selectedProjectSource.asObservable();

  /**
   * The selected project name, read synchronously.
   *
   * The observable is the right thing for reacting to a change; this is for
   * callers that need the current value while BUILDING a request, where
   * subscribing would mean threading an async hop through a params helper.
   */
  get selectedProjectName(): string {
    return this.selectedProjectSource.value;
  }

  private uploadedProjectSource = new BehaviorSubject<string>('');
  uploadedProject = this.uploadedProjectSource.asObservable();

  /**
   * Refetch-on-scrub-back seam.
   *
   * Row storage is now a bounded ring buffer (see TelemetryRingBuffer), so
   * scrubbing to a tick older than the retention window finds nothing in memory.
   * The chart components emit here; MainLayoutComponent answers by re-issuing the
   * historical backfill.
   *
   * NOTE: the API exposes no tick-range query — GetTemperatureData takes only
   * `lastEpocTime` (rows AFTER an epoch) or 0 (everything). So the only refetch
   * available today is a FULL reload, which is why this is throttled hard. When a
   * server-side windowed/range endpoint lands, narrow this to the requested span
   * and drop the throttle.
   */
  /**
   * Independent poll cursors, one per telemetry family. The three server HWMs are
   * computed independently and are NOT guaranteed to advance together, so a single
   * shared cursor silently skips rows in whichever family lags.
   */
  readonly cursors = new TelemetryCursorSet();

  /** Cursor for a family's next poll. */
  getCursor(family: TelemetryFamily): number {
    return this.cursors.for(family);
  }

  /** Advance a family's cursor from its own response. */
  advanceCursor(
    family: TelemetryFamily,
    data: { HWM?: number } | null | undefined,
    fallbackEpoch = 0
  ): number {
    return this.cursors.advance(family, data, fallbackEpoch);
  }

  /**
   * Append the spatial-downsampling query parameters (telemetry item 3).
   *
   * Omitting them entirely is the backward-compatible full-resolution request, so
   * a null `params` leaves the query exactly as it was before item 3 existed.
   * `windowEnd` is exclusive, matching the server's [start, end) slice.
   */
  private withDownsample(
    params: HttpParams,
    downsample?: DownsampleParams | null
  ): HttpParams {
    if (!downsample || downsample.resolution <= 0) {
      return params;
    }
    return params
      .set('resolution', downsample.resolution)
      .set('windowStart', downsample.windowStart)
      .set('windowEnd', downsample.windowEnd);
  }

  /**
   * Time one telemetry response and feed the adaptive controller.
   *
   * THE measurement chokepoint. Every family's request passes through here, so the
   * throughput EWMA sees one clean sample per response without any component — or
   * any of the three chart families — knowing the controller exists.
   *
   * Sizing the response is a cascade, because a cross-origin response usually hides
   * its own size:
   *
   *   1. Resource Timing `transferSize` — needs Timing-Allow-Origin
   *   2. `Content-Length`               — needs Access-Control-Expose-Headers
   *   3. estimate from the payload shape
   *
   * (3) always works, and it is the same arithmetic the bytes-per-point EWMA is
   * calibrated against, so measured and estimated samples stay on one scale. The
   * Functions host sets neither CORS header today, so in practice (3) is what runs;
   * (1) and (2) sharpen the numbers automatically if that ever changes.
   */
  private inFlightTelemetrySource = new BehaviorSubject<number>(0);
  /** Number of telemetry requests currently in flight — drives the spinner. */
  readonly inFlightTelemetry = this.inFlightTelemetrySource.asObservable();

  /**
   * The spinner answers ONE question: is this project still opening?
   *
   * Not "is anything being fetched". A scrub-back history reload, a zoom refetch
   * and a catch-up read are all historical, and raising the overlay for each made
   * it flash repeatedly through a session — which reads as the app restarting
   * rather than as progress. Once the opening load has settled, the project IS
   * open, and later fetches are refinements the user did not ask to wait for.
   */
  private initialLoadDone = false;

  /** Project change reopens the question. */
  resetInitialLoad(): void {
    this.initialLoadDone = false;
    this.inFlightTelemetrySource.next(0);
  }

  private beginTelemetryRequest(): void {
    if (this.initialLoadDone) {
      return;
    }
    this.inFlightTelemetrySource.next(this.inFlightTelemetrySource.value + 1);
  }

  private endTelemetryRequest(): void {
    // finalize() runs on completion, error AND unsubscribe, so a cancelled
    // request cannot strand the counter above zero and leave the spinner up
    // forever. Clamped anyway: a stuck spinner is a worse bug than a missed one.
    if (this.initialLoadDone) {
      return;
    }
    const next = Math.max(0, this.inFlightTelemetrySource.value - 1);
    this.inFlightTelemetrySource.next(next);
    if (next === 0) {
      // The opening load has settled. Everything after this is a refinement, so
      // the overlay stays down for the rest of the project.
      this.initialLoadDone = true;
    }
  }

  /**
   * The call path that reached a fetch, minus this service's own frames.
   *
   * Cheap enough to leave on: it runs once per historical request, and a request
   * costs orders of magnitude more than reading a stack.
   */
  private callerFrames(): string {
    try {
      const raw = new Error().stack ?? '';
      const frames = raw
        .split('\n')
        .slice(1)
        .map((f) => f.trim().replace(/^at\s+/, ''))
        .filter((f) => !f.includes('callerFrames') && !f.includes('shareHistorical'))
        .slice(0, 4);
      return frames.join(' <- ') || 'unknown';
    } catch {
      return 'unknown';
    }
  }


  /**
   * One historical fetch per request identity, per project selection.
   *
   * This used to collapse only CONCURRENT duplicates, holding an in-flight map
   * that deleted its entry the moment a request settled. Two callers that
   * overlapped got one request; two callers a second apart got two — and the
   * open-project path was being scheduled twice, roughly a second apart, with
   * byte-identical parameters. So the guard missed the duplicate it most needed
   * to catch, and did so by construction rather than by accident.
   *
   * HistoricalCacheService holds the payload for the life of the selection
   * instead, which makes the collapse independent of timing: the second asker is
   * answered from memory whether it arrives 5 ms or 5 s later. It also turns
   * cross-tab navigation into an in-memory hit, since a window and resolution
   * already fetched for one tab is the same bytes for the next.
   *
   * Keyed on the full url and params, so a genuinely different window or
   * resolution still goes out — this removes redundancy, never coverage.
   */
  /**
   * Hold a data request until the measurement preference is KNOWN.
   *
   * THE DEFECT THIS REMOVES. unitCode is resolved when a request is built, from
   * isMetricUnit, which follows PreferencesService.effective$ — and effective$
   * opens on a 'metric' placeholder that is indistinguishable from a real metric
   * preference. On a soft navigation the preference had already arrived and the
   * request went out imperial; on a full reload the same URL raced the
   * preferences fetch and went out metric. Measured on local:4300: identical URL,
   * identical session, imperial on SPA nav and metric after F5.
   *
   * WHY GATING RATHER THAN RE-REQUESTING. The server converts every distance and
   * temperature it returns according to unitCode, so a request made with the
   * wrong one is wholly wrong, not partially. Re-issuing on arrival means a
   * second full round-trip on every load AND a visible metric-to-imperial flip
   * on screen. Waiting cannot flip, because nothing is drawn from the wrong
   * answer in the first place.
   *
   * THE FAST PATH IS SYNCHRONOUS. Once settled — which for a returning user is
   * before the first request, since load() now hydrates the cache up front —
   * this returns build() directly with no scheduler tick, so the steady state
   * and every realtime poll are exactly as they were.
   *
   * THE WAIT IS BOUNDED. If preferences never settle (no identity, dead
   * endpoint), the timer wins and the request proceeds on the default. Degrading
   * to today's behaviour is acceptable; never loading is not, and an unbounded
   * gate in front of every telemetry request is the kind of thing that takes a
   * demo down.
   */
  /**
   * Hold CHART CONSTRUCTION until the measurement preference is known.
   *
   * THE HALF afterUnitsSettle DID NOT COVER. That gate defers the data REQUEST,
   * so the numbers come back in the right units. It does nothing about the axis
   * LABELS, which are baked in when the SciChartSurface is built from
   * isMetricUnit read synchronously — and on a cold client that read happens
   * before the preference has arrived, so it takes the 'metric' placeholder.
   * Nothing rebuilds the axes afterwards: the only recreate path is a PROJECT
   * change (setupProjectSubscription), and the units settling is not one.
   *
   * The result is the worst available outcome — imperial DATA under metric
   * LABELS. Measured on beta-dev and prod2 on 2026-09-16, same project, same
   * session: with a warm cache the marker reads '33.30ft'; with the preference
   * cache cleared it reads '32.81m', and 32.81 is 10 / 0.3048. The value is
   * feet, the suffix says metres. A reader has no way to tell, which is what
   * makes it worse than a blank chart.
   *
   * WHY WAIT RATHER THAN RECREATE ON ARRIVAL, matching afterUnitsSettle's
   * reasoning: recreating means disposing a live WASM surface and rebuilding it
   * a few hundred ms in, which costs a visible flash and a second data bind.
   * Waiting cannot flash, because nothing is drawn in the wrong units.
   *
   * SYNCHRONOUS ONCE SETTLED, so the steady state and every in-app navigation
   * are unchanged; only a genuine cold start pays anything.
   *
   * BOUNDED by the same UNITS_SETTLE_TIMEOUT_MS. If preferences never settle the
   * chart is built on the default, which is exactly today's behaviour — an
   * unbounded wait in front of chart construction would mean a permanently
   * blank dashboard, and that is not an improvement on a wrong label.
   */
  async whenUnitsSettled(): Promise<void> {
    if (this.preferencesService.settled$.value) {
      return;
    }
    await firstValueFrom(
      race(
        this.preferencesService.settled$.pipe(
          filter((settled) => settled),
          take(1)
        ),
        timer(ProjectService.UNITS_SETTLE_TIMEOUT_MS)
      ).pipe(take(1))
    );
    this.warnIfUnitsUnsettled('chart construction');
  }

  /**
   * SAY SO when the bound expired, because the failure is otherwise invisible.
   *
   * An expired gate renders a complete, internally consistent, entirely wrong
   * unit system. There is no mismatch to notice and no error to read — the only
   * way anyone learns is by knowing what the account was set to. A console line
   * is not a fix, but it turns "the units are just wrong" into one grep.
   */
  private warnIfUnitsUnsettled(where: string): void {
    if (!this.preferencesService.settled$.value) {
      console.warn(
        `UNITS NOT SETTLED after ${ProjectService.UNITS_SETTLE_TIMEOUT_MS}ms — ` +
          `${where} is proceeding on the METRIC DEFAULT and may not match the ` +
          `saved preference.`
      );
    }
  }

  private afterUnitsSettle<T>(build: () => Observable<T>): Observable<T> {
    if (this.preferencesService.settled$.value) {
      return build();
    }
    return race(
      this.preferencesService.settled$.pipe(
        filter((settled) => settled),
        take(1)
      ),
      timer(ProjectService.UNITS_SETTLE_TIMEOUT_MS)
    ).pipe(
      take(1),
      switchMap(() => {
        this.warnIfUnitsUnsettled('a data request');
        return build();
      })
    );
  }

  private shareHistorical<T>(key: string, source: () => Observable<T>): Observable<T> {
    // WHO ASKED, HOW MANY TIMES, AND WITH WHAT KEY.
    //
    // The count is per endpoint since load and is the invariant to read off a
    // capture: it should reach 1 per family per open and stop. The FULL key is
    // logged because two requests can look identical on the wire and still miss
    // the cache — tenantId and unitCode are resolved when the request is built,
    // and both can change during bootstrap as MSAL claims and preferences
    // arrive. If a count goes to 2, diffing the two keys says which param moved
    // rather than leaving it to be guessed.
    const endpoint = key.split('?')[0].split('/').pop() ?? key;
    const n = (ProjectService.historicalFetchCounts[endpoint] =
      (ProjectService.historicalFetchCounts[endpoint] ?? 0) + 1);
    console.log(`HISTORICAL-FETCH #${n} ${endpoint} <- ` + this.callerFrames());

    return this.historicalCache.share(key, source);
  }

  private measureTelemetry<T>(
    source: Observable<T>,
    url: string,
    downsample: DownsampleParams | null | undefined,
    rowsOf: (payload: T) => { rows: number; pointsPerRow: number },
    isRealtimeRequest = false
  ): Observable<T> {
    const startedAt = performance.now();
    // COUNT EVERY TELEMETRY REQUEST, here and only here.
    //
    // The loading overlay used to bind to flags that main-layout sets, which
    // covers a project change but NOT a tab switch: zone and loss are fetched by
    // the tab components themselves, so opening /health or /curing for the first
    // time showed empty charts with no indication anything was happening.
    //
    // Every telemetry call already passes through this wrapper, so counting here
    // catches all of them — including any added later, which a list of flags at
    // the call sites would not.
    // ONLY HISTORICAL REQUESTS RAISE THE SPINNER.
    //
    // Counting every telemetry call made the pinwheel reappear on each realtime
    // poll and each zoom refetch — a flicker every few seconds for the life of a
    // live cure, which is worse than no indicator at all because it reads as the
    // app restarting. The spinner answers "is the project still opening?", and
    // only the historical backfill can answer yes.
    const historical = !isRealtimeRequest;
    if (historical) {
      this.beginTelemetryRequest();
    }
    return source.pipe(
      finalize(() => {
        if (historical) {
          this.endTelemetryRequest();
        }
      }),
      tap((payload) => {
        const elapsedMs = performance.now() - startedAt;
        const shape = rowsOf(payload);
        if (shape.rows <= 0) {
          return;
        }
        const bytes =
          this.transferSizeOf(url) ??
          shape.rows * shape.pointsPerRow * BYTES_PER_POINT_ESTIMATE;

        this.adaptive.recordSample({
          bytes,
          elapsedMs,
          rows: shape.rows,
          // Report the resolution we ASKED for, not the width we got back: a
          // full-resolution response's width is the fixed DTS array, which says
          // nothing about the per-point cost of a budget we chose.
          resolution: downsample?.resolution ?? 0,
        });
      })
    );
  }

  /** Wire size from Resource Timing, when the server allows reading it. */
  private transferSizeOf(url: string): number | null {
    try {
      const entries = performance.getEntriesByName(url) as PerformanceResourceTiming[];
      const last = entries[entries.length - 1];
      // 0 means the timing is opaque (no Timing-Allow-Origin), not "empty body".
      if (last && typeof last.transferSize === 'number' && last.transferSize > 0) {
        return last.transferSize;
      }
    } catch {
      // Resource Timing unavailable — fall through to the estimate.
    }
    return null;
  }

  /**
   * Attach the caller's identity to a telemetry request.
   *
   * The telemetry endpoints have carried no identity at all — only `tenantId` and
   * the function key — so the server has had nothing to authorise against. This is
   * the client half of closing that: one chokepoint, so every family's request
   * gains identity together and none can be forgotten.
   *
   * Two identities travel, and which one the server honours depends on the
   * PROJECT'S COMPANY, not on anything the client asserts:
   *
   *   Entra-tied company   the `Authorization: Bearer` header, attached by
   *                        VericureAuthInterceptor (every DataServices URL is
   *                        already covered by the protectedResourceMap prefix).
   *                        objectId is REJECTED for these companies, which is what
   *                        makes them unspoofable — a bearer cannot be invented,
   *                        an objectId can.
   *   Google / DB company  `objectId`, since these sessions have no MSAL account
   *                        and therefore no bearer to send.
   *
   * objectId is sent whenever one resolves, including for Entra sessions. It costs
   * nothing — for an Entra-tied company the server ignores it in favour of the
   * bearer — and it avoids the client having to guess which kind of company a
   * project belongs to, which is precisely the judgement the server is taking over.
   * GetProjectlist already sends both under RequireEntraAuth, so this matches
   * established behaviour rather than inventing a second convention.
   *
   * Safe to ship ahead of the server: enforcement starts flag-off, and an
   * unrecognised query parameter is ignored by the current build.
   */
  private withIdentity(params: HttpParams): HttpParams {
    let objectId = '';
    try {
      objectId = this.authService?.getObjectId?.() ?? '';
    } catch {
      // Identity resolution must never break a data call; the server decides
      // what an unidentified request may have.
      objectId = '';
    }
    return objectId ? params.set('objectId', objectId) : params;
  }

  /**
   * THE TEMPORAL RANGE PARAMS, agreed with admin-cosmos and deployed server-side.
   *
   *   fromEpochMs  EXCLUSIVE (timestamp >)   absent = unbounded
   *   toEpochMs    INCLUSIVE (timestamp <=)  absent = unbounded
   *
   * The asymmetry is deliberate. `from` is a CURSOR — the same `timestamp >`
   * serves backfill and live poll, and the strict > is what stops the handoff
   * repeating the boundary tick. `to` is a BOUND, and the last tick of a cure is
   * the one the operator came for; exclusive would drop it silently.
   *
   * Distinct from windowStart/windowEnd, which also travel on these endpoints and
   * are SPATIAL sample indices for the decimator. Two orthogonal axes.
   *
   * NOTHING SENDS THESE TODAY, and that is a decision rather than a gap.
   * The defect they were built for — the server bounding reads at
   * `Now.AddDays(-15)`, so every finished cure answered empty — was fixed
   * server-side, and `projectName` already scopes a read to one project's
   * lifetime. Both bounds the client could derive were measured to LOSE data:
   * a name-derived start is viewer-timezone-dependent and clipped 49 minutes off
   * a real project, and a last-telemetry end rides a value known to drift behind
   * the true final tick. See project-lifetime.ts for the measurements.
   *
   * They remain wired for PAGING, where a caller supplies a range it actually
   * knows rather than one inferred from metadata.
   */
  private static readonly LIFETIME_PARAMS = {
    start: 'fromEpochMs',
    end: 'toEpochMs',
  } as const;

  /** The range in force for historical reads. Unbounded unless a caller sets one. */
  private range: EpochRange = { fromMs: null, toMs: null };

  /**
   * Constrain historical reads to an explicit range.
   *
   * Each end is independent: null means unbounded on that side, never "constrain
   * to nothing". Absent-versus-empty has bitten this estate before.
   */
  setHistoricalRange(range: EpochRange): void {
    this.range = range;
  }

  /** The range currently in force. Read by specs and traces. */
  get historicalRange(): EpochRange {
    return this.range;
  }

  /** Attach the temporal bounds to a historical request, when a caller set any. */
  private withRange(params: HttpParams, isRealtime = false): HttpParams {
    // A REALTIME POLL IS NOT A RANGE READ. It asks for rows after a cursor, and
    // an upper bound at the last row we already hold would ask for nothing.
    if (isRealtime || !isBounded(this.range)) {
      return params;
    }
    let next = params;
    if (this.range.fromMs !== null) {
      next = next.set(ProjectService.LIFETIME_PARAMS.start, this.range.fromMs);
    }
    if (this.range.toMs !== null) {
      next = next.set(ProjectService.LIFETIME_PARAMS.end, this.range.toMs);
    }
    return next;
  }

  /** Historical requests per endpoint since load; see shareHistorical(). */
  private static historicalFetchCounts: Record<string, number> = {};

  /** Snapshot of the per-endpoint historical counts, for assertions. */
  static historicalCounts(): Record<string, number> {
    return { ...ProjectService.historicalFetchCounts };
  }

  /** Families already warned about an unseeded realtime cursor. */
  private static warnedZeroCursor = new Set<string>();

  /** Clear every cursor — project change. */
  resetCursors(): void {
    this.cursors.reset();
  }

  private historyReloadSource = new Subject<string>();
  historyReload = this.historyReloadSource.asObservable();
  private lastHistoryReloadAt = 0;
  private readonly HISTORY_RELOAD_THROTTLE_MS = 15000;

  /** Ask for the historical backfill again because `reason` needs evicted rows. */
  requestHistoryReload(reason: string): void {
    const now = Date.now();
    if (now - this.lastHistoryReloadAt < this.HISTORY_RELOAD_THROTTLE_MS) {
      return;
    }
    this.lastHistoryReloadAt = now;
    // A reload is a request for data we deliberately no longer trust — a chart
    // scrubbed below the retention window, or the curing baseline. Without this
    // the cache would answer it with the very payload that was found wanting.
    this.historicalCache.invalidate(undefined, `history reload: ${reason}`);
    console.log('ProjectService > history reload requested:', reason);
    this.historyReloadSource.next(reason);
  }

  private temperatureDataSource = new BehaviorSubject<ProjectTemperatureData>({
    TRC: [],
    SI: 0,
    MI: 0,
    CRB: 0,
    CRE: 0,
    CRS: 0,
    LSS: 0,
    LES: 0,
    CT: 0,
    OT: 0,
    SER: '',
    FIB: '',
    HMC: [],
    MAR: [],
    N: [],
    U: [],
    LG: '',
    VS: 0,
  });
  temperatureData = this.temperatureDataSource.asObservable();

  private temperatureRTDataSource = new BehaviorSubject<ProjectTemperatureData>(
    {
      TRC: [],
      SI: 0,
      MI: 0,
      CRB: 0,
      CRE: 0,
      CRS: 0,
      LSS: 0,
      LES: 0,
      CT: 0,
      OT: 0,
      SER: '',
      FIB: '',
      HMC: [],
      MAR: [],
      N: [],
      U: [],
      LG: '',
      VS: 0,
    }
  );
  temperatureRTData = this.temperatureRTDataSource.asObservable();

  private lossDataSource = new BehaviorSubject<ProjectLossData>({
    LRC: [],
    SI: 0,
    MI: 0,
    CRB: 0,
    CRE: 0,
    CRS: 0,
    LSS: 0,
    LES: 0,
    CT: 0,
    MIR: false,
  });
  lossData = this.lossDataSource.asObservable();

  private lossRTDataSource = new BehaviorSubject<ProjectLossData>({
    LRC: [],
    SI: 0,
    MI: 0,
    CRB: 0,
    CRE: 0,
    CRS: 0,
    LSS: 0,
    LES: 0,
    CT: 0,
    MIR: false,
  });
  lossRTData = this.lossRTDataSource.asObservable();

  private zoneDataSource = new BehaviorSubject<ProjectZoneData>({
    ZRC: [],
    SI: 0,
    MI: 0,
    CRB: 0,
    CRE: 0,
    CRS: 0,
    LSS: 0,
    LES: 0,
    CT: 0,
  });
  zoneData = this.zoneDataSource.asObservable();

  private zoneRTDataSource = new BehaviorSubject<ProjectZoneData>({
    ZRC: [],
    SI: 0,
    MI: 0,
    CRB: 0,
    CRE: 0,
    CRS: 0,
    LSS: 0,
    LES: 0,
    CT: 0,
  });
  zoneRTData = this.zoneRTDataSource.asObservable();

  /**
   * ZONES ARE DERIVED IN THE BROWSER (Joel, 2026-09-24). The zone family's rows
   * are no longer read from GetHeatmapData: they are folded here from raw stored
   * temperatures (GetTemperatureData raw=1) with the desktop rule, and published
   * on the same zone subjects, so the strip, the store and the PDF prerender
   * need no change. See zone-fold.ts and zone-derivation.ts.
   */
  private zoneDerivation: ZoneDerivation | null = null;
  /** The unitCode the last HISTORICAL temperature request actually sent. */
  private lastHistoricalTempUnitCode: number | null = null;
  /** Zones folded from the display backfill, awaiting the zone load that uses them. */
  private displayZones: { project: string; window: LinerWindow; result: DisplayDerivation; hwm?: number } | null = null;
  /**
   * Which path each zone backfill took: 'display' (zero extra bytes) or 'raw'
   * (raw=1 chunks). Measurable, per Joel's bandwidth constraint.
   */
  readonly zoneSourceCounts = { display: 0, raw: 0 };
  lastZoneSource: { path: 'display' | 'raw'; reason?: string } | null = null;
  /** The liner window from the last temperature backfill, per project. */
  private zoneLinerWindow: { project: string; window: LinerWindow } | null = null;
  private zoneDerivationStatusSource = new BehaviorSubject<{
    state: 'idle' | 'ready' | 'unavailable';
    reason?: string;
  }>({ state: 'idle' });
  /** Whether the strip's zones are exact, and if not, WHY. Never a silent blank. */
  zoneDerivationStatus$ = this.zoneDerivationStatusSource.asObservable();

  private notesDataSource = new BehaviorSubject<Note[]>([]);
  notesData = this.notesDataSource.asObservable();

  private tickPositionSource = new BehaviorSubject<number>(0);
  tickPosition = this.tickPositionSource.asObservable();

  private vert_annot_positionSource = new BehaviorSubject<number>(10);
  vert_annot_position = this.vert_annot_positionSource.asObservable();

  private vert_annot_positionSources = new BehaviorSubject<MarkerInfo[]>([]);
  vert_annot_positions = this.vert_annot_positionSources.asObservable();

  private fiberFaultLevelSource = new BehaviorSubject<number>(0);
  fiberFaultLevel = this.fiberFaultLevelSource.asObservable();

  private cmsFaultLevelSource = new BehaviorSubject<number>(0);
  cmsFaultLevel = this.cmsFaultLevelSource.asObservable();

  private deviceStatusSource = new BehaviorSubject<DeviceStatus>('unknown');
  deviceStatus$ = this.deviceStatusSource.asObservable();
  private lastDataReceivedTime: number = 0;
  private consecutiveEmptyResponses: number = 0;
  private readonly EMPTY_RESPONSE_THRESHOLD = 3; // Require 3+ empty responses before considering offline
  private readonly OFFLINE_TIMEOUT_MS = 60000; // 60 seconds to mark truly offline

  constructor(private http: HttpClient, private authService: UserAuthService, private preferencesService: PreferencesService, private adaptive: AdaptiveResolutionService, private historicalCache: HistoricalCacheService) {
    this.followPreferredUnits();
  }

  /**
   * UNITS FOLLOW THE PREFERENCE CASCADE unless this project overrides them.
   *
   * THE GAP THIS CLOSES: isMetricUnit and toUpload_isMetricUnit were initialised
   * to `true` and only ever moved by the units radio on /projects. So an account
   * whose company preference was IMPERIAL opened the app in metric, picked a
   * project, and got metric — the preference existed, was resolved, and reached
   * nothing that renders. getProjectUnitPreference() consulted it, but the value
   * every chart, axis and export actually reads is isMetricUnit, and that never
   * heard about it.
   *
   * PRECEDENCE, deliberately: a per-project choice beats the cascade. Someone who
   * switched THIS project to imperial to read it alongside a customer's paperwork
   * has made a specific decision about a specific cure, and a preference refresh
   * must not silently undo it. The cascade supplies the default, which is what a
   * default is.
   *
   * Only the ACTIVE project's units move. Stored per-project choices are left
   * alone, so switching away and back does not lose them.
   */
  private followPreferredUnits(): void {
    this.preferencesService.effective$
      .pipe(
        map((prefs) => prefs.measurementSystem !== 'imperial'),
        distinctUntilChanged()
      )
      .subscribe((preferredIsMetric) => {
        const active = this.getProject();
        if (active && this.projectUnitPreferences.has(active)) return;
        this.isMetricUnit = preferredIsMetric;
        this.toUpload_isMetricUnit = preferredIsMetric;
      });
  }

  /**
   * Get the effective tenant ID to use for API calls
   * @param providedTenantId - Optional tenant ID provided by caller
   * @returns The tenant ID to use for API calls
   */
  private getEffectiveTenantId(providedTenantId?: string): string {
    // Priority order:
    // 1. Provided tenant ID (from parameter)
    // 2. Tenant ID from auth service (from login response)
    // 3. Hardcoded fallback tenant ID
    
    if (providedTenantId) {
      return providedTenantId;
    }
    
    const authTenantId = this.authService.getCurrentCustomerTenantId();
    if (authTenantId) {
      return authTenantId;
    }
    
    return this.tenandId; // Fallback to hardcoded value
  }

  /**
   * @param quiet REFRESH WITHOUT DISTURBING WHAT IS ON SCREEN.
   *
   * A background refetch — the live project-list push, or a reconnect catch-up —
   * must not do the two things a first load does. It must not emit `loading`,
   * which makes the grid flash its spinner on every signal; and it must not empty
   * `projectList` when the request fails, which would blank a populated grid on
   * one transient blip. Both are correct for a first load, where there is nothing
   * to preserve and the user is waiting, and both are wrong for a refresh.
   *
   * On a quiet failure the last good list simply stays, and the push is an
   * optimisation rather than a source of truth — a missed refresh is corrected by
   * the next signal or the next navigation.
   */
  loadProjectList(objectId: string, CustomerTenantId?: string, quiet = false) {
    const blocked = projectListBlockReason(
      this.authService.getRoles(),
      this.authService.getActiveCustomerId()
    );
    if (blocked) {
      // NOT SENT, and said so. A refused call must not look like an empty list.
      console.warn(`ProjectService > GetProjectlist NOT sent: ${blocked}`);
      if (!quiet) {
        this.projectListLoadSource.next({
          kind: 'failed',
          status: 0,
          message:
            blocked === 'company_selection_required'
              ? 'Choose a company to see its projects.'
              : 'Your account permissions are still being checked. Please try again in a moment.',
          detail: `${blocked}: GetProjectlist was not sent`,
        });
      }
      return;
    }
    if (!quiet) {
      this.projectListLoadSource.next({ kind: 'loading' });
    }
    this.getClientIp().subscribe(
      (clientIp) => {
        // Use provided CustomerTenantId or get effective tenant ID
        const effectiveCustomerTenantId = this.getEffectiveTenantId(CustomerTenantId);
        
        const activeCustomerId = this.authService.getActiveCustomerId();
        let params = new HttpParams()
          .set('tenantId', effectiveCustomerTenantId)
          .set('objectId', objectId)
          .set('clientIp', clientIp);
        if (activeCustomerId) {
          params = params.set('customerId', activeCustomerId);
        }
        const options = { params };

        if (!quiet) {
          console.log('ProjectService > Loading projects for customer tenant:', effectiveCustomerTenantId, 'customerId:', activeCustomerId);
        }

        this.http
          .get<ProjectInfoResponse[]>(environment.apiUrl + 'GetProjectlist', options)
          .subscribe(
            (data) => {
              // A QUIET refetch now runs every 45 s on every open tab. At ~7
              // lines per project this block logged roughly 375 lines a minute
              // for a 53-project tenant — enough on its own to evict everything
              // else from the 200-entry console buffer, which is how a decisive
              // capture was lost earlier today. A first load still logs in full;
              // only the background refresh is mute.
              const log = quiet
                ? (..._args: unknown[]) => {}
                : (...args: unknown[]) => console.log(...args);

              log('ProjectService > Raw API response:', data);
              log('ProjectService > Response type:', typeof data);
              log('ProjectService > Is array:', Array.isArray(data));

              if (data && Array.isArray(data)) {
                // NOTHING CHANGED, SO TELL NOBODY.
                //
                // Without this the 45 s refetch republishes 80 times an hour for
                // a list that is usually identical, and every republish is a
                // fresh array of ProjectInfo instances for the GC plus a full
                // grid rebuild in every subscriber.
                //
                // QUIET ONLY. A first load, a customer switch and the retry
                // button must always publish: restoreProjectOrShowPicker takes
                // exactly ONE emission and would otherwise wait out its timeout
                // and show neither content nor a picker.
                const signature = projectListSignature(data);
                if (quiet && isUnchangedList(this.projectListSignature, signature)) {
                  return;
                }
                this.projectListSignature = signature;

                // Convert API response to ProjectInfo instances
                this.projectList = data.map(item => {
                  log('ProjectService > Processing item (full object):', JSON.stringify(item));
                  
                  // Handle both old and new API response formats
                  let projectData: ProjectInfoResponse;
                  const anyItem = item as any;
                  
                  // Check which format we have by looking at the actual fields
                  // The API might return: Name/PNAME/projectName, SER/SerialNumber/serialNumber, LTR/LastTelemetryReceived/lastTelemetryReceived
                  const projectName = anyItem.projectName || anyItem.PNAME || anyItem.Name || anyItem.ProjectName;
                  const serialNumber = anyItem.serialNumber || anyItem.SER || anyItem.SerialNumber || anyItem.DTSSerial;
                  const lastTelemetry = anyItem.lastTelemetryReceived || anyItem.LTR || anyItem.LastTelemetryReceived;

                  // Telemetry-DERIVED liveness fields (MAX(timestamp) from
                  // Telemetry_v2 + the project's measurement interval). These
                  // supersede the stored LTR, which is unreliable — it lags in
                  // some tenants and leads by months in others. Alias-tolerant and
                  // null-safe so the client works before AND after the server ships
                  // them.
                  const lastTelemetryEpochMs =
                    anyItem.lastTelemetryEpochMs ??
                    anyItem.LastTelemetryEpochMs ??
                    anyItem.lastTelemetryEpoch ??
                    null;
                  const measurementInterval =
                    anyItem.MI ?? anyItem.mi ?? anyItem.measurementInterval ?? null;
                  // SERVER-DECIDED liveness (finished flag + 48 h + latest-per-CMS).
                  const serverIsLive =
                    anyItem.isLive ?? anyItem.IsLive ?? anyItem.live ?? null;

                  log('ProjectService > Field extraction:');
                  log('  - ProjectName:', projectName, 'from', { projectName: anyItem.projectName, PNAME: anyItem.PNAME, Name: anyItem.Name, ProjectName: anyItem.ProjectName });
                  log('  - SerialNumber:', serialNumber, 'from', { serialNumber: anyItem.serialNumber, SER: anyItem.SER, SerialNumber: anyItem.SerialNumber, DTSSerial: anyItem.DTSSerial });
                  log('  - LastTelemetry:', lastTelemetry, 'from', { lastTelemetryReceived: anyItem.lastTelemetryReceived, LTR: anyItem.LTR, LastTelemetryReceived: anyItem.LastTelemetryReceived });

                  // Decode FiberType if it's base64 encoded
                  let fiberType = anyItem.fiberType || anyItem.FiberType || '';
                  if (anyItem.Registry?.FiberType) {
                    try {
                      fiberType = atob(anyItem.Registry.FiberType);
                    } catch (e) {
                      fiberType = anyItem.Registry.FiberType;
                    }
                  }

                  const customerName = anyItem.customerName || anyItem.CustomerName || '';
                  // Friendly CMS name, joined server-side onto GetProjectlist.
                  // Alias-tolerant like every other field here because this
                  // mapper is an ALLOWLIST: anything not named below is dropped,
                  // however faithfully the server sent it.
                  const deviceName = anyItem.deviceName || anyItem.DeviceName || '';
                  log('ProjectService > CustomerName from API:', anyItem.customerName, '-> mapped to:', customerName);

                  projectData = {
                    PNAME: projectName,
                    ProjectName: projectName,
                    SER: serialNumber,
                    SerialNumber: serialNumber,
                    LTR: lastTelemetry,
                    LastTelemetryReceived: lastTelemetry,
                    lastTelemetryEpochMs:
                      typeof lastTelemetryEpochMs === 'number' ? lastTelemetryEpochMs : null,
                    MI: typeof measurementInterval === 'number' ? measurementInterval : null,
                    isLive: typeof serverIsLive === 'boolean' ? serverIsLive : null,
                    FiberType: fiberType,
                    Version: anyItem.version || anyItem.Version || anyItem.MeasuredLinerVersion || '',
                    CustomerName: customerName,
                    DeviceName: deviceName,
                    SamplingInterval: anyItem.samplingInterval ?? anyItem.SamplingInterval ?? null,
                    CuringTemperature: anyItem.curingTemperature ?? anyItem.CuringTemperature ?? null,
                    OverheatTemperature: anyItem.overheatTemperature ?? anyItem.OverheatTemperature ?? null,
                    LinerLength: anyItem.linerLength ?? anyItem.LinerLength ?? null,
                    CmsModel: anyItem.cmsModel || anyItem.CmsModel || '',
                    CmsFirmware: anyItem.cmsFirmware || anyItem.CmsFirmware || '',
                    Finalized: anyItem.finalized ?? anyItem.Finalized ?? null,
                    UserParameter: anyItem.userParameter || anyItem.UserParameter || []
                  };
                  
                  log('ProjectService > Mapped projectData (with CustomerName):', projectData);
                  
                  const projectInfo = new ProjectInfo(projectData);
                  log('ProjectService > Created ProjectInfo (LTR=' + projectInfo.LTR + ', LastTelemetryReceived=' + projectInfo.LastTelemetryReceived + ')');
                  return projectInfo;
                }).filter((item): item is ProjectInfo => item !== null);
                
                log('ProjectService > Final project list:', this.projectList);
                this.projectListLoadSource.next({
                  kind: 'loaded',
                  count: this.projectList.length,
                });
              } else {
                console.error('ProjectService > Invalid API response format:', data);
                if (quiet) {
                  // Keep the list that is already on screen. See `quiet` above.
                  return;
                }
                this.projectList = [];
                // Forget what was published, so the next good response is
                // republished even if it is byte-identical to the last one.
                this.projectListSignature = null;
                this.projectListLoadSource.next(
                  describeProjectListFailure(undefined, 'The server returned an unexpected response.')
                );
              }
            },
            (error) => {
              console.error('Error loading project list:', error);
              if (quiet) {
                // A background refresh that fails leaves the previous list alone.
                // Blanking a populated grid because one poll blipped is worse than
                // showing data a few seconds stale.
                return;
              }
              this.projectList = [];
              this.projectListSignature = null;
              // The server usually says WHY — "No devices found for customer" is
              // a diagnosis, not noise. Emptying the list and swallowing it is
              // what cost Joel an afternoon.
              this.projectListLoadSource.next(describeProjectListFailure(error));
            }
          );
      },
      (error) => {
        // Unreachable in practice now that getClientIp catches — kept so a future
        // change to that pipe cannot re-create a silent dead end.
        console.error('Error getting client IP:', error);
        this.projectListLoadSource.next(
          describeProjectListFailure(error, 'Could not start loading your projects.')
        );
      }
    );
  }

  getClientIp() {
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    const requestOptions: Object = {
      headers: headers,
      responseType: 'text',
    };
    return this.http
      .get<any>('https://api.ipify.org/?format=text', requestOptions)
      .pipe(
        catchError((err) => {
          // of(''), NOT ''. RxJS treats a bare string as an ObservableInput and
          // iterates it, so `return ''` built an observable that completes
          // WITHOUT EMITTING. The subscriber's next handler never ran, its error
          // handler never ran either — so when ipify was unreachable,
          // loadProjectList silently never issued its request at all. No list, no
          // error, no log. clientIp is diagnostic; losing it must not cost the
          // user their projects.
          console.warn('ProjectService > client IP lookup failed; continuing without it', err?.status ?? err);
          return of('');
        })
      );
  }

  validateProject(projName: string): Observable<number> {
    const options = {
      params: new HttpParams()
        .set('tenantId', this.getEffectiveTenantId())
        .set('projectName', projName),
    };

    var result = this.http.get<number>(
      environment.apiUrl + 'ValidateProjectSubscription',
      options
    );

    return result;
  }

  loadTemperatureData(
    projName: string,
    isRealtime: boolean,
    lastEpocTime: number,
    downsample?: DownsampleParams | null
  ): Observable<ProjectTemperatureData> {
    // DIAGNOSTIC (temporary): every realtime fetch, whatever calls it. Placed at
    // the service rather than the call sites so a caller cannot be missed.
    if (isRealtime) {
      // THE GAP, enforced where the request actually leaves the browser.
      //
      // A guard on the callers is only as good as the list of callers. This is
      // the one function every realtime fetch passes through — the same place
      // the instrumentation counts them — so nothing can poll faster than the
      // cadence by adding a subscriber. Too soon and no HTTP request is made at
      // all: EMPTY completes without emitting, which every caller already
      // handles, since they only act inside next().
      // A cursor of 0 means the backfill has not seeded it yet. Polling then
      // asks for the WHOLE history on the realtime path and races the backfill
      // already in flight.
      //
      // Under gate-on-live this is close to unreachable — fetchRealTimeData is
      // only set after the loss payload lands, by which point the temperature
      // backfill has seeded the cursor. Kept anyway, and moved HERE rather than
      // deleted with the caller gate: it costs one comparison, it has never
      // actually been observed holding (every build that carried it also had the
      // storm masking it), and a full-history request on the realtime path is
      // the single most expensive thing this client can do by accident.
      if (lastEpocTime <= 0) {
        ProjectService.rtPollSuppressed++;
        return EMPTY;
      }

      const now = Date.now();
      const sinceLast = now - this.lastRealtimeFetchAt;
      if (sinceLast < this.realtimeFetchGapMs()) {
        ProjectService.rtPollSuppressed++;
        return EMPTY;
      }
      this.lastRealtimeFetchAt = now;

      ProjectService.rtPollCount++;
      console.trace(
        `RT-POLL fire #${ProjectService.rtPollCount} cursor=${lastEpocTime} ` +
          `project=${projName} gap=${sinceLast}ms cadence=${this.realtimeFetchGapMs()}ms ` +
          `suppressed-so-far=${ProjectService.rtPollSuppressed}`
      );
    }
    return this.afterUnitsSettle(() => {
      const options = {
        params: this.withRange(
          this.withIdentity(
            this.withDownsample(
              new HttpParams()
                .set('tenantId', this.getEffectiveTenantId())
                .set('projectName', projName)
                .set('unitCode', this.recordUnit(isRealtime, this.isMetricUnit ? 0 : 1))
                .set('isRealtime', isRealtime)
                .set('lastEpocTime', lastEpocTime),
              downsample
            )
          ),
          isRealtime
        ),
      };

      const url = environment.apiUrl + 'GetTemperatureData';
      // MEASURE INSIDE THE CACHE. measureTelemetry raises the spinner when it is
      // CALLED and records a bandwidth sample when the payload lands, so wrapping
      // the cache in it would charge every hit an extra spinner tick and — worse —
      // feed the adaptive sampler a sample with real bytes and a ~0 ms elapsed,
      // which reads as infinite bandwidth and pushes resolution up. A hit must
      // replay an already-measured payload and do nothing else.
      const fetchOnce = () =>
        this.measureTelemetry(
          this.http.get<ProjectTemperatureData>(url, options),
          url,
          downsample,
          (payload) => ({
            rows: payload?.TRC?.length ?? 0,
            pointsPerRow: payload?.TRC?.[0]?.TR?.length ?? 0,
          }),
          // A realtime poll is not the project opening; it must not raise the
          // spinner. This loader serves both, so it forwards its own flag.
          isRealtime
        );
      var result = isRealtime
        ? fetchOnce()
        : this.shareHistorical(`${url}?${options.params.toString()}`, fetchOnce);

      // var result = this.http.get<ProjectTemperatureData>(
      //   'http://localhost:7071/api/ReadNotes'
      // );
      return result;

      // result.subscribe(
      //   (data) => {
      //     this.temperatureDataSource.next(data);
      //     //console.log('Data Received');
      //   },
      //   (error) => console.log(error)
      // );
    });
  }

  /**
   * THE SINGLE RETRIEVAL PATH for a historical backfill.
   *
   * Both the live open and the PDF pre-render come through here. They used not
   * to: the pre-render carried its own copy of the temperature fetch that did
   * setTempData and loadMarkers but NOT advanceCursor, NOT the live seed and NOT
   * notes. The consequence was concrete rather than cosmetic — a report
   * generated during a live cure left report-resolution rows in the buffer with
   * the cursor still pointing at the live position, so the next tick appended
   * live rows onto report rows in one series with nothing marking the seam.
   *
   * Two copies of a fetch will always drift; the fix is one copy, not a careful
   * second one. The seed stays with the caller because LiveTelemetryService is a
   * main-layout concern and the pre-render has no business seeding a live gate —
   * so this returns the cursor rather than applying it.
   */
  ingestTemperatureHistorical(data: ProjectTemperatureData): number {
    // FRONT-TRUNCATION GUARD. This is the single retrieval path for a historical
    // backfill, so every full-lifetime temperature payload passes through here —
    // which makes it the one place that sees them all and can notice when one
    // starts later than another for the same project.
    //
    // Temperature only: loss rows carry no epoch, so there is nothing to compare
    // them by. That limits what this can prove and is better than a check that
    // pretends otherwise.
    this.checkWindowIntegrity(data);
    this.setTempData(data);
    const cursor = this.advanceCursor('temperature', data, maxEpochOf(data));
    if (data?.N) {
      this.setNotesData(data.N);
    }
    if (data?.MAR) {
      this.loadMarkers(data.MAR);
    }
    return cursor;
  }

  /**
   * Fetch one family's backfill and ingest it, whoever is asking.
   *
   * Zone and loss already publish and advance their own cursors inside their
   * loaders, so those are pass-throughs; temperature is the one that had a
   * hand-rolled second copy. Returns when the data has been ingested, which is
   * what a caller that must then capture a chart needs.
   */
  async ensureHistorical(
    project: string,
    family: TelemetryFamily,
    downsample: DownsampleParams | null
  ): Promise<HistoricalPayload | null> {
    if (!project) {
      return null;
    }
    if (family === 'temperature') {
      const data = await firstValueFrom(
        this.loadTemperatureData(project, false, 0, downsample)
      );
      // Zones from these SAME rows first, before anything else reads them: zero
      // extra bytes when they are metric and unthinned (see deriveFromDisplay).
      if (typeof data?.LSS === 'number' && typeof data?.LES === 'number' && data.LES > data.LSS) {
        const window = { start: data.LSS, end: data.LES };
        this.zoneLinerWindow = { project, window };
        this.displayZones = {
          project,
          window,
          result: deriveFromDisplay(project, window, data as never, this.lastHistoricalTempUnitCode),
          hwm: data?.HWM,
        };
      } else {
        this.displayZones = null;
      }
      this.ingestTemperatureHistorical(data);
      return data;
    }
    if (family === 'zone') {
      return firstValueFrom(this.loadZoneData$(project, downsample));
    }
    return firstValueFrom(this.loadLossData$(project, downsample));
  }

  /**
   * How many rows a backfill actually returned.
   *
   * A family is EMPTY when the server answered with no rows — which is a real
   * answer, not a failure, and is what beta-dev returns for every project today.
   * Distinguishing it from "not asked" is the whole point: the previous cascade
   * could only express "no rows arrived", so a project with genuinely no data was
   * indistinguishable from one whose load never ran.
   */
  static rowsIn(family: TelemetryFamily, payload: HistoricalPayload | null): number {
    if (!payload) {
      return 0;
    }
    if (family === 'temperature') {
      return (payload as ProjectTemperatureData).TRC?.length ?? 0;
    }
    if (family === 'zone') {
      return (payload as ProjectZoneData).ZRC?.length ?? 0;
    }
    return (payload as ProjectLossData).LRC?.length ?? 0;
  }

  /**
   * Compare this payload's first tick against the earliest seen for the project.
   *
   * A backfill asks for the whole lifetime, so a later start than a previous
   * backfill means the server's bound moved forward and the front of the cure is
   * missing — the defect admin-cosmos measured at 48 minutes. It reports through
   * console.error so the browser gate fails on it rather than a person having to
   * spot a shorter chart.
   */
  private checkWindowIntegrity(data: ProjectTemperatureData): void {
    const first = data?.TRC?.[0]?.T;
    const tick = first instanceof Date ? first.getTime() : Date.parse(String(first ?? ''));
    reportWindowStart(
      checkWindowStart({
        project: this.selectedProjectName || this.getProject() || '',
        source: 'temperature backfill',
        renderedFirstTick: tick,
        // A historical backfill is by definition the whole lifetime; a zoom
        // refetch does not come through this path.
        claimsFullLifetime: true,
      })
    );
  }

  setTempData(temp: ProjectTemperatureData) {
    // Capture the cadence from the payload — it is what paces the fallback poll.
    this.noteMeasurementInterval(temp?.MI);
    this.temperatureDataSource.next(temp);
    // Store sampling interval for marker positioning
    if (temp.SI) {
      this.samplingInterval = temp.SI;
    }
  }

  setRTTempData(temp: ProjectTemperatureData) {
    this.noteMeasurementInterval(temp?.MI);
    this.temperatureRTDataSource.next(temp);
    // Store sampling interval for marker positioning
    if (temp.SI) {
      this.samplingInterval = temp.SI;
    }
  }

  /**
   * Push a zone RT payload (item 4 live stream). Mirrors what loadZoneDataRT does
   * on the poll path, so live and polled zone rows reach the charts identically.
   */
  setRTZoneData(zone: ProjectZoneData) {
    this.zoneRTDataSource.next(zone);
  }

  /** Push a loss RT payload (item 4 live stream). Poll-path equivalent of above. */
  setRTLossData(loss: ProjectLossData) {
    this.lossRTDataSource.next(loss);
  }

  setNotesData(notes: Note[]) {
    this.notesDataSource.next(notes);
  }

  loadZoneData(projName: string, downsample?: DownsampleParams | null) {
    this.loadZoneData$(projName, downsample).subscribe({
      error: (error) => console.log(error),
    });
  }

  /**
   * Zone backfill: fetch, advance the cursor, publish — and HAND BACK the rows.
   *
   * The void-returning `loadZoneData` could only ever be fire-and-forget, so the
   * only way to know it had finished was to subscribe to the subject it writes.
   * That is precisely the trigger this redesign removes: an emission on a
   * replaying BehaviorSubject is not the completion of a named load. A caller
   * that can AWAIT the fetch does not need to listen for its side effect.
   *
   * Returning the payload is also what lets a consumer with no charts mounted —
   * the PDF pre-render today, a stats or server-side report path later — use the
   * one retrieval path instead of growing a second copy of it.
   */
  loadZoneData$(
    projName: string,
    downsample?: DownsampleParams | null
  ): Observable<ProjectZoneData> {
    // NO afterUnitsSettle HERE, unlike every other family. The raw request always
    // asks unitCode=0 and the fold needs no conversion, so the unit preference
    // cannot change it. The display geometry is copied from the temperature
    // payload, which already waited for the units.
    {
      // The window the zone rows are placed on: the liner, from the temperature
      // backfill, or the explicit request window when one was passed.
      const window: LinerWindow | null =
        downsample && downsample.windowEnd > downsample.windowStart && downsample.windowStart >= 0
          ? { start: downsample.windowStart, end: downsample.windowEnd }
          : this.zoneLinerWindow?.project === projName
            ? this.zoneLinerWindow.window
            : null;

      if (!window) {
        this.zoneDerivation = null;
        return of(this.publishZones(projName, [], undefined, 'unavailable',
          'The liner window is not known yet, so zones cannot be derived.', false));
      }

      // ZERO EXTRA BYTES when the temperature backfill already carried the stored
      // values for exactly this window. One-shot: consumed here.
      const fromDisplay = this.displayZones;
      this.displayZones = null;
      if (
        fromDisplay &&
        fromDisplay.project === projName &&
        fromDisplay.window.start === window.start &&
        fromDisplay.window.end === window.end
      ) {
        if (fromDisplay.result.ok) {
          this.zoneSourceCounts.display++;
          this.lastZoneSource = { path: 'display' };
          console.info(`ZONES ${projName}: derived from the display rows (0 extra bytes)`);
          this.zoneDerivation = fromDisplay.result.derivation;
          return of(this.publishZones(projName, fromDisplay.result.rows, fromDisplay.hwm, 'ready', undefined, false));
        }
        this.lastZoneSource = { path: 'raw', reason: fromDisplay.result.reason };
      } else {
        this.lastZoneSource = { path: 'raw', reason: 'no display rows for this project and window' };
      }
      this.zoneSourceCounts.raw++;
      console.info(`ZONES ${projName}: raw=1 chunks, because ${this.lastZoneSource.reason}`);

      // A backfill starts a fresh derivation: the fold is cumulative from tick 0.
      //
      // IN SPATIAL CHUNKS, ONE AT A TIME (Joel's bandwidth and heap constraint).
      // Each chunk is sized from the tick count so its raw JSON stays near
      // RAW_VALUES_PER_REQUEST values. It is folded and released before the next
      // is requested, so the heap never holds more than one chunk's raw rows.
      const expectedTicks = this.temperatureDataSource.getValue()?.TRC?.length ?? 1;
      const chunks = chunkWindow(window, expectedTicks);
      this.zoneDerivation = new ZoneDerivation(projName, window, chunks);
      const derivation = this.zoneDerivation;
      let lastHwm: number | undefined;
      return from(chunks.map((chunk, index) => ({ chunk, index }))).pipe(
        concatMap(({ chunk, index }) =>
          this.fetchRawTemperature(projName, chunk, false, 0).pipe(
            map((resp) => {
              derivation.ingestChunk(index, resp);
              lastHwm = resp?.HWM ?? lastHwm;
              return null; // the raw response goes out of scope here
            })
          )
        ),
        last(),
        map(() => {
          try {
            const rows = derivation.emitNewRows();
            return this.publishZones(projName, rows, lastHwm, 'ready', undefined, false);
          } catch (e) {
            if (!(e instanceof ZoneFoldRefused)) throw e;
            console.error(`ZONES UNAVAILABLE for ${projName}: ${e.message}`);
            return this.publishZones(projName, [], undefined, 'unavailable', e.message, false);
          }
        }),
        catchError((err) => {
          if (err instanceof ZoneFoldRefused) {
            // The data was refused, not the request: say what was wrong with it.
            console.error(`ZONES UNAVAILABLE for ${projName}: ${err.message}`);
            return of(this.publishZones(projName, [], undefined, 'unavailable', err.message, false));
          }
          // SAY IT, then still fail the family: a raw read that errored is not
          // "no zones", and must not look like it.
          this.publishZones(projName, [], undefined, 'unavailable', rawFailureReason(err), false);
          return throwError(() => err);
        })
      );
    }
  }

  /**
   * Raw stored temperatures for the zone fold: GetTemperatureData raw=1.
   * unitCode 0 and no resolution, so nothing asks the server to convert or decimate.
   */
  private fetchRawTemperature(
    projName: string,
    window: LinerWindow,
    isRealtime: boolean,
    lastEpocTime: number
  ): Observable<RawTemperatureResponse> {
    let params = this.withIdentity(
      new HttpParams()
        .set('tenantId', this.getEffectiveTenantId())
        .set('projectName', projName)
        .set('unitCode', 0)
        .set('isRealtime', isRealtime)
        .set('lastEpocTime', lastEpocTime)
        .set('raw', 1)
        .set('windowStart', window.start)
        .set('windowEnd', window.end)
    );
    if (!isRealtime) {
      params = this.withRange(params);
    }
    return this.http.get<RawTemperatureResponse>(environment.apiUrl + 'GetTemperatureData', { params });
  }

  /** Remember the unit a historical temperature request sent; returns it unchanged. */
  private recordUnit(isRealtime: boolean, unitCode: number): number {
    if (!isRealtime) {
      this.lastHistoricalTempUnitCode = unitCode;
    }
    return unitCode;
  }

  /** Fold a raw response into zone rows and publish them, refusing loudly. */
  private deriveZones(
    derivation: ZoneDerivation,
    resp: RawTemperatureResponse,
    realtime: boolean
  ): ProjectZoneData {
    try {
      const rows = derivation.ingest(resp);
      return this.publishZones(derivation.project, rows, resp?.HWM, 'ready', undefined, realtime);
    } catch (e) {
      if (!(e instanceof ZoneFoldRefused)) {
        throw e;
      }
      console.error(`ZONES UNAVAILABLE for ${derivation.project}: ${e.message}`);
      return this.publishZones(derivation.project, [], undefined, 'unavailable', e.message, realtime);
    }
  }

  private publishZones(
    projName: string,
    rows: ZoneDataRow[],
    hwm: number | undefined,
    state: 'ready' | 'unavailable',
    reason: string | undefined,
    realtime: boolean
  ): ProjectZoneData {
    // Geometry in DISPLAY units, from the temperature payload the strip already
    // places itself against. The raw response's own geometry is unconverted.
    const t = this.temperatureDataSource.getValue();
    const zone: ProjectZoneData = {
      SI: t?.SI,
      MI: t?.MI,
      CRB: t?.CRB,
      CRE: t?.CRE,
      CRS: t?.CRS,
      LSS: t?.LSS,
      LES: t?.LES,
      CT: t?.CT,
      ZRC: rows,
      ...(typeof hwm === 'number' ? { HWM: hwm } : {}),
    } as ProjectZoneData;
    this.zoneDerivationStatusSource.next({ state, reason });
    if (typeof hwm === 'number') {
      this.advanceCursor('zone', zone);
    }
    (realtime ? this.zoneRTDataSource : this.zoneDataSource).next(zone);
    return zone;
  }

  /** Zone RT poll. Uses the ZONE cursor, independent of temperature's. */
  loadZoneDataRT(projName: string, downsample?: DownsampleParams | null) {
    const lastEpocTime = this.getCursor('zone');
    // NEVER POLL REALTIME ON A ZERO CURSOR.
    //
    // lastEpocTime=0 on the realtime path asks for the WHOLE cure, every cycle —
    // the single most expensive thing this client can do by accident, and the
    // temperature loader has refused it for exactly that reason. Zone and loss
    // had no such guard, which did not matter only because the socket being live
    // suppressed the poll entirely. The moment the socket drops or the gate
    // opens, an unseeded cursor would turn a stalled chart into a request storm.
    //
    // TRADE-OFF, STATED PLAINLY: zone and loss cursors seed ONLY from data.HWM,
    // and the backfills are not currently sending it (observed cursors
    // zone:0 loss:0 against a healthy temp cursor). So this guard does not merely
    // prevent a storm, it suppresses the fallback poll for these two families
    // until a cursor exists. That is the lesser harm — the socket is the primary
    // path and a storm is unrecoverable without a reload — but it is a REAL loss
    // of fallback, so it says so once per family rather than failing silently.
    // The durable fix is HWM on the zone and loss backfill responses (DS).
    if (lastEpocTime <= 0) {
      ProjectService.rtPollSuppressed++;
      if (!ProjectService.warnedZeroCursor.has('zone')) {
        ProjectService.warnedZeroCursor.add('zone');
        console.warn(
          `RT-CURSOR ZONE realtime poll suppressed: cursor is 0, so a poll would ` +
            `request the entire cure every cycle. This family has no fallback ` +
            `until its backfill returns an HWM — push is the only path.`
        );
      }
      return;
    }

    // Derived zones: poll the RAW temperatures after the zone cursor and fold
    // them onto the running derivation. Without a derivation for this project
    // there is no fold to extend; the next backfill starts one.
    const derivation = this.zoneDerivation;
    if (!derivation || derivation.project !== projName || derivation.refused) {
      return;
    }
    // NOT CACHED. A realtime poll is the freshness mechanism.
    this.fetchRawTemperature(projName, derivation.window, true, lastEpocTime).subscribe(
      (resp) => this.deriveZones(derivation, resp, true),
      (error) => {
        // The cursor did not move, so the next poll retries these ticks; the fold
        // is NOT poisoned. Until then the strip says the update failed.
        console.error(`ZONES live update failed for ${projName}`, error);
        this.publishZones(projName, [], undefined, 'unavailable', rawFailureReason(error), true);
      }
    );
  }

  loadLossData(projName: string, downsample?: DownsampleParams | null) {
    this.loadLossData$(projName, downsample).subscribe({
      error: (error) => console.log(error),
    });
  }

  /** Loss backfill: fetch, advance the cursor, publish — and hand back the rows. */
  loadLossData$(
    projName: string,
    downsample?: DownsampleParams | null
  ): Observable<ProjectLossData> {
    return this.afterUnitsSettle(() => {
      const options = {
        params: this.withRange(
          this.withIdentity(
          this.withDownsample(
            new HttpParams()
              .set('tenantId', this.getEffectiveTenantId())
              .set('projectName', projName)
              .set('unitCode', this.isMetricUnit ? 0 : 1)
              .set('language', this.selectedLang)
              .set('isRealtime', false)
              .set('lastEpocTime', 0),
            downsample
          )
          )
        ),
      };

      const url = environment.apiUrl + 'GetLossData';
      var result = this.shareHistorical(
        `${url}?${options.params.toString()}`,
        () =>
          this.measureTelemetry(
            this.http.get<ProjectLossData>(url, options),
            url,
            downsample,
            (payload) => ({
          rows: payload?.LRC?.length ?? 0,
          pointsPerRow: payload?.LRC?.[0]?.LR?.length ?? 0,
            })
          )
      );

      return result.pipe(
        tap((data) => {
          // Loss rows carry no epoch either — HWM only.
          this.advanceCursor('loss', data);
          this.lossDataSource.next(data);
        })
      );
    });
  }

  /** Loss RT poll. Uses the LOSS cursor, independent of temperature's. */
  /**
   * Observable-returning loss fetch for the zoom refetch (telemetry item 3).
   *
   * `loadLossData` pushes into the shared subject, which would make every loss
   * consumer re-render from a window only the health chart asked for. This returns
   * the response to the caller instead, leaving the subject untouched.
   */
  loadLossDataWindow(
    projName: string,
    downsample: DownsampleParams
  ): Observable<ProjectLossData> {
    return this.afterUnitsSettle(() => {
      const options = {
        params: this.withIdentity(
          this.withDownsample(
            new HttpParams()
              .set('tenantId', this.getEffectiveTenantId())
              .set('projectName', projName)
              .set('unitCode', this.isMetricUnit ? 0 : 1)
              .set('language', this.selectedLang)
              .set('isRealtime', false)
              .set('lastEpocTime', 0),
            downsample
          )
        ),
      };
      const url = environment.apiUrl + 'GetLossData';
      return this.shareHistorical(
        `${url}?${options.params.toString()}`,
        () =>
          this.measureTelemetry(
            this.http.get<ProjectLossData>(url, options),
            url,
            downsample,
            (payload) => ({
              rows: payload?.LRC?.length ?? 0,
              pointsPerRow: payload?.LRC?.[0]?.LR?.length ?? 0,
            })
          )
      );
    });
  }

  loadLossRTData(projName: string, downsample?: DownsampleParams | null) {
    const lastEpocTime = this.getCursor('loss');
    // NEVER POLL REALTIME ON A ZERO CURSOR.
    //
    // lastEpocTime=0 on the realtime path asks for the WHOLE cure, every cycle —
    // the single most expensive thing this client can do by accident, and the
    // temperature loader has refused it for exactly that reason. Zone and loss
    // had no such guard, which did not matter only because the socket being live
    // suppressed the poll entirely. The moment the socket drops or the gate
    // opens, an unseeded cursor would turn a stalled chart into a request storm.
    //
    // TRADE-OFF, STATED PLAINLY: zone and loss cursors seed ONLY from data.HWM,
    // and the backfills are not currently sending it (observed cursors
    // zone:0 loss:0 against a healthy temp cursor). So this guard does not merely
    // prevent a storm, it suppresses the fallback poll for these two families
    // until a cursor exists. That is the lesser harm — the socket is the primary
    // path and a storm is unrecoverable without a reload — but it is a REAL loss
    // of fallback, so it says so once per family rather than failing silently.
    // The durable fix is HWM on the zone and loss backfill responses (DS).
    if (lastEpocTime <= 0) {
      ProjectService.rtPollSuppressed++;
      if (!ProjectService.warnedZeroCursor.has('loss')) {
        ProjectService.warnedZeroCursor.add('loss');
        console.warn(
          `RT-CURSOR LOSS realtime poll suppressed: cursor is 0, so a poll would ` +
            `request the entire cure every cycle. This family has no fallback ` +
            `until its backfill returns an HWM — push is the only path.`
        );
      }
      return;
    }

    const options = {
      params: this.withIdentity(
        this.withDownsample(
          new HttpParams()
            .set('tenantId', this.getEffectiveTenantId())
            .set('projectName', projName)
            .set('unitCode', this.isMetricUnit ? 0 : 1)
            .set('language', this.selectedLang)
            .set('isRealtime', true)
            .set('lastEpocTime', lastEpocTime),
          downsample
        )
      ),
    };

    const url = environment.apiUrl + 'GetLossData';
    // NOT CACHED. A realtime poll is the freshness mechanism; holding its
    // payload for the life of the selection would freeze the live chart at
    // whatever the first poll happened to return.
    var result = this.measureTelemetry(
      this.http.get<ProjectLossData>(url, options),
      url,
      downsample,
      (payload) => ({
        rows: payload?.LRC?.length ?? 0,
        pointsPerRow: payload?.LRC?.[0]?.LR?.length ?? 0,
      }),
      true
    );

    result.subscribe(
      (data) => {
        this.advanceCursor('loss', data);
        this.lossRTDataSource.next(data);
      },
      (error) => console.log(error)
    );
  }

  // Notes are now loaded from GetTemperatureData API response (N property)
  // Removed loadNotesData and loadRealtimeNotesData methods

  uploadProject(fileName: string, content: FormData) {
    const options = {
      params: new HttpParams()
        .set('tenantId', this.getEffectiveTenantId())
        .set('projectName', fileName),
      observe: 'events',
      reportProgress: true,
    };

    var result = this.http.post<any>(
      environment.apiUrl + 'UploadProject',
      content,
      {
        params: new HttpParams()
          .set('tenantId', this.getEffectiveTenantId())
          .set('projectName', fileName),
        observe: 'events',
        reportProgress: true,
      }
    );
    return result;
  }


  setUploadedProject(projName: string) {
    this.uploadedProjectSource.next(projName);
  }

  getTempData() {
    return this.temperatureDataSource.getValue();
  }

  setTick(position: number) {
    this.tickPositionSource.next(position);
    //console.log('Service > tickPositionSource updated');
  }

  getTick() {
    return this.tickPositionSource.getValue();
  }

  setProject(projName: string) {
    // A new selection owns new bytes; nothing cached for the old one applies.
    this.historicalCache.selectProject(projName);
    this.selectedProjectSource.next(projName);
    //console.log('Service >  selectedProjectSource updated');
  }

  getProject() {
    return this.selectedProjectSource.getValue();
  }

  setVerticalAnnotationPosition(position: number) {
    this.vert_annot_positionSource.next(position);
  }

  getVerticalAnnotationPosition() {
    return this.vert_annot_positionSource.getValue();
  }

  addMarker() {
    const currentMarkers = this.vert_annot_positionSources.getValue();
    
    // Calculate position using collision detection logic
    const newPosition = this.calculateMarkerPosition(currentMarkers);
    
    // Get color for the new marker using HSL gradient - ensures unique colors
    const totalColors = 18; // Total colors in the gradient
    const colorIndex = currentMarkers.length;
    const rgbColor = this.getColorGradient(colorIndex, totalColors);
    
    console.log(`Adding marker #${colorIndex} with unique color: ${rgbColor} at position: ${newPosition}m`);
    
    const newMarker: MarkerInfo = {
      P: newPosition,
      C: rgbColor
    };
    
    // Add new marker to the list
    const allMarkers = [...currentMarkers, newMarker];
    
    // Get the current project name
    const projectName = this.getProject();
    
    if (!projectName) {
      console.error('No project selected. Cannot add marker.');
      return;
    }
    
    // Prepare the API URL and payload in the format expected by the API
    const apiUrl = `${environment.apiUrl}v1/projects/markers`;
    const params = new HttpParams().set('projectName', projectName);
    
    // Convert to API format: Position and Color instead of P and C
    const apiPayload = allMarkers.map(marker => ({
      Position: marker.P,
      Color: marker.C
    }));
    
    // Call the API with all markers
    console.log('Saving markers to API:', apiUrl);
    console.log('Project Name:', projectName);
    console.log('New marker position (meters):', newPosition);
    console.log('Payload:', apiPayload);
    
    this.http.post(apiUrl, apiPayload, { params }).subscribe(
      (response) => {
        console.log('Markers saved successfully:', response);
        // Update the markers data in the service to reflect the changes
        this.vert_annot_positionSources.next(allMarkers);
      },
      (error) => {
        console.error('Error saving markers:', error);
        alert('Failed to save marker. Please try again.');
      }
    );
  }

  /**
   * Calculate the position for a new marker using collision detection
   * @param existingMarkers Array of existing markers
   * @returns Position in meters for the new marker
   */
  private calculateMarkerPosition(existingMarkers: MarkerInfo[]): number {
    let candidatePosition = this.INITIAL_MARKER_POSITION; // Start at 10 meters
    
    // Convert to samples for collision detection
    let candidateSample = this.metersToSamples(candidatePosition);
    
    // Keep checking positions until we find one without collision
    let attempts = 0;
    const maxAttempts = 1000; // Safety limit to avoid infinite loop
    
    while (attempts < maxAttempts) {
      // Check if this position collides with any existing marker
      const hasCollision = this.checkCollision(candidateSample, existingMarkers);
      
      if (!hasCollision) {
        // No collision found, use this position
        console.log(`Marker position calculated: ${candidatePosition}m (sample: ${candidateSample}), attempts: ${attempts + 1}`);
        return candidatePosition;
      }
      
      // Collision detected, move 1 meter to the right
      candidatePosition += this.MARKER_INCREMENT;
      candidateSample = this.metersToSamples(candidatePosition);
      attempts++;
    }
    
    console.warn('Max attempts reached for marker positioning, using last candidate position');
    return candidatePosition;
  }

  /**
   * Check if a candidate position collides with existing markers
   * @param candidateSample Position in samples
   * @param existingMarkers Array of existing markers
   * @returns true if collision detected, false otherwise
   */
  private checkCollision(candidateSample: number, existingMarkers: MarkerInfo[]): boolean {
    const minSpacingSamples = this.metersToSamples(this.MINIMUM_MARKER_SPACING);
    
    for (const marker of existingMarkers) {
      const markerSample = this.metersToSamples(marker.P);
      const distance = Math.abs(candidateSample - markerSample);
      
      if (distance < minSpacingSamples) {
        return true; // Collision detected
      }
    }
    
    return false; // No collision
  }

  /**
   * Convert meters to samples using the sampling interval
   * @param meters Position in meters
   * @returns Position in samples
   */
  private metersToSamples(meters: number): number {
    if (this.samplingInterval <= 0) {
      console.warn('Invalid sampling interval, using default 0.15m');
      return meters / 0.15;
    }
    return meters / this.samplingInterval;
  }

  /**
   * Convert samples to meters using the sampling interval
   * @param samples Position in samples
   * @returns Position in meters
   */
  private samplesToMeters(samples: number): number {
    if (this.samplingInterval <= 0) {
      console.warn('Invalid sampling interval, using default 0.15m');
      return samples * 0.15;
    }
    return samples * this.samplingInterval;
  }

  loadMarkers(markers: MarkerInfo[]) {
    console.log('ProjectService > loadMarkers called with:', markers);
    console.log('ProjectService > Number of markers:', markers?.length);
    this.vert_annot_positionSources.next(markers);
  }

  getVerticalAnnotationPositions() {
    return this.vert_annot_positionSources.getValue();
  }
  
  private hexToRgb(hex: string): string {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse hex to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return `${r}, ${g}, ${b}`;
  }
  
  /**
   * Generate a color using HSL gradient for better color distribution
   * @param index The index of the marker
   * @param totalColors Total number of colors in the gradient (default 18)
   * @returns RGB color string in format "r, g, b"
   */
  private getColorGradient(index: number, totalColors: number = 18): string {
    // Calculate hue based on index to distribute colors evenly around the color wheel
    const hue = (index * 360 / totalColors) % 360;
    const saturation = 100; // Full saturation for vibrant colors
    const lightness = 50;   // Medium lightness for visibility
    
    // Convert HSL to RGB
    const rgb = this.hslToRgb(hue, saturation, lightness);
    
    return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
  }
  
  /**
   * Convert HSL color values to RGB
   * @param h Hue (0-360)
   * @param s Saturation (0-100)
   * @param l Lightness (0-100)
   * @returns RGB object with r, g, b values (0-255)
   */
  private hslToRgb(h: number, s: number, l: number): { r: number, g: number, b: number } {
    // Normalize values
    s = s / 100;
    l = l / 100;
    
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;
    
    let r = 0, g = 0, b = 0;
    
    if (h >= 0 && h < 60) {
      r = c; g = x; b = 0;
    } else if (h >= 60 && h < 120) {
      r = x; g = c; b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0; g = c; b = x;
    } else if (h >= 180 && h < 240) {
      r = 0; g = x; b = c;
    } else if (h >= 240 && h < 300) {
      r = x; g = 0; b = c;
    } else if (h >= 300 && h < 360) {
      r = c; g = 0; b = x;
    }
    
    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255)
    };
  }
  
  private getMarkerColors(): string[] {
    return [
      '#7F30F3',
      '#63EE55',
      '#F00049',
      '#17A4E5',
      '#C6FA64',
      '#BB12BB',
      '#4BF0C8',
      '#F99230',
      '#2E2CC3',
      '#26C462',
      '#B5146E',
      '#40ECFA',
      '#E7E04F',
    ];
  }

  getMarkerColorForIndex(index: number) {
    const colors = this.getMarkerColors();
    return colors[index % colors.length];
  }

  /**
   * Publish the marker list to every chart.
   *
   * THE ONE PLACE a marker change becomes visible to other surfaces. Move goes
   * through it today; DELETE is not implemented yet and when it is, it calls
   * this and needs no other wiring — that is the point of the seam.
   *
   * Emits a NEW ARRAY. The callers mutate marker objects in place on the array
   * the BehaviorSubject already holds, so re-emitting the same reference is
   * indistinguishable from the previous value to any consumer that compares
   * references — the change would land in the store and never reach the chart.
   * The marker OBJECTS are deliberately not cloned: `C` is the identity a colour
   * is keyed on, and copying them would break that identity for no gain.
   */
  private publishMarkers(markers: MarkerInfo[]): void {
    this.vert_annot_positionSources.next([...markers]);
  }

  updateVerticalAnnotationPosition(index: number, position: number) {
    var curPositions = this.vert_annot_positionSources.getValue();

    if (curPositions.length > index) {
      curPositions[index].P = position;
      
      // Get the current project name
      const projectName = this.getProject();
      
      if (!projectName) {
        console.error('No project selected. Cannot update marker position.');
        return;
      }
      
      // Prepare the API URL and payload in the format expected by the API
      const apiUrl = `${environment.apiUrl}v1/projects/markers`;
      const params = new HttpParams().set('projectName', projectName);
      
      // Convert to API format: Position and Color instead of P and C
      const apiPayload = curPositions.map(marker => ({
        Position: marker.P,
        Color: marker.C
      }));
      
      // Call the API to persist all markers with updated positions
      console.log('Updating marker positions to API:', apiUrl);
      console.log('Project Name:', projectName);
      console.log('Updated marker index:', index, 'New position (meters):', position);
      console.log('Payload:', apiPayload);
      
      this.http.post(apiUrl, apiPayload, { params }).subscribe(
        (response) => {
          console.log('Marker positions updated successfully:', response);
          // TELL EVERY CHART, NOT JUST THE ONE THAT WAS DRAGGED.
          //
          // This used to end here, with the comment "the marker is already moved
          // by the drag operation". That is true only of the surface the drag
          // happened on, which moves its own annotation locally. Every OTHER
          // subscriber — the /graph temp-over-time traces, the /temperature
          // lines — was never told, so dragging a marker on the curing chart
          // left the temperature chart pointing at the old position. That is
          // what Rob Stensby saw on the call.
          //
          // Emitted AFTER the write succeeds, deliberately: subscribers then
          // render the position the server actually holds, and a failed write
          // leaves every chart agreeing on the old one rather than one chart
          // silently disagreeing. It is also OFF the drag callback stack, which
          // matters because BehaviorSubject delivers synchronously — emitting
          // inline from onDragEnded would re-enter the curing subscriber and
          // rebuild the very annotation whose drag handler was still running.
          this.publishMarkers(curPositions);
        },
        (error) => {
          console.error('Error updating marker positions:', error);
          alert('Failed to update marker position. Please try again.');
        }
      );
    }
  }

  setFiberFaultLevel(level: number) {
    this.fiberFaultLevelSource.next(level);
  }

  setCmsFaultLevel(level: number) {
    this.cmsFaultLevelSource.next(level);
  }

  setDeviceOnline() {
    const now = Date.now();
    // Record the arrival so the cadence can be MEASURED rather than believed.
    if (this.lastDataReceivedTime > 0 && now > this.lastDataReceivedTime) {
      this.arrivalTimes.push(now);
      if (this.arrivalTimes.length > ProjectService.ARRIVAL_WINDOW) {
        this.arrivalTimes.shift();
      }
    } else if (this.arrivalTimes.length === 0) {
      this.arrivalTimes.push(now);
    }
    this.lastDataReceivedTime = now;
    this.consecutiveEmptyResponses = 0; // Reset counter on data received
    if (this.deviceStatusSource.value !== 'online') {
      this.deviceStatusSource.next('online');
    }
  }

  checkDeviceTimeout(thresholdMs: number = 30000) {
    if (this.lastDataReceivedTime === 0) return;

    // Increment empty response counter
    this.consecutiveEmptyResponses++;

    // Only mark offline if:
    // 1. We've had 3+ consecutive empty responses AND
    // 2. 60+ seconds have elapsed since last data
    const elapsed = Date.now() - this.lastDataReceivedTime;
    const shouldMarkOffline =
      this.consecutiveEmptyResponses >= this.EMPTY_RESPONSE_THRESHOLD &&
      elapsed >= this.OFFLINE_TIMEOUT_MS &&
      this.deviceStatusSource.value !== 'offline';

    if (shouldMarkOffline) {
      console.log(`Device timeout: ${this.consecutiveEmptyResponses} empty responses, ${(elapsed / 1000).toFixed(1)}s elapsed`);
      this.deviceStatusSource.next('offline');
    }
  }

  resetDeviceStatus() {
    this.arrivalTimes = [];
    this.lastDataReceivedTime = 0;
    this.deviceStatusSource.next('unknown');
  }

  /**
   * How long without data before the device is declared offline.
   *
   * DERIVED FROM THE RIG'S OWN CADENCE, never a constant. Joel's rig ticks about
   * every 62 s; another ships at 10 s. A fixed timeout either false-alarms on the
   * slow one or never fires on the fast one, and we have both in the field.
   *
   * Two missed intervals plus half of one as grace: a single late tick is jitter,
   * two in a row is a pattern. Floored so a rig reporting a nonsensically small MI
   * cannot make the pill flicker.
   */
  /**
   * The MEASURED interval between data arrivals, in ms, or null if we have not
   * seen enough to say. Median, not mean: one 116 s gap must not drag the
   * estimate for a stream that is otherwise steady.
   */
  observedCadenceMs(): number | null {
    if (this.arrivalTimes.length < ProjectService.MIN_ARRIVALS_FOR_CADENCE) {
      return null;
    }
    return medianGapMs(this.arrivalTimes, ProjectService.MIN_ARRIVALS_FOR_CADENCE);
  }

  /**
   * How long without data before the device is declared offline, or null when we
   * cannot responsibly say.
   *
   * OBSERVED CADENCE, NOT MI. MI is stored per project and is measurably wrong:
   * 5 s claimed against a 14.5 s median, with real gaps to 116.1 s. Deriving the
   * threshold from it would put "Offline" on the header of a healthy project.
   * So the threshold comes from the spacing we have actually seen, and when we
   * have not seen enough intervals to measure one, this returns null and the
   * caller holds 'unknown' rather than asserting a state it cannot support.
   */
  deviceOfflineThresholdMs(): number | null {
    const observed = this.observedCadenceMs();
    if (observed === null) {
      return null;
    }
    return Math.max(
      ProjectService.MIN_OFFLINE_THRESHOLD_MS,
      Math.round(observed * 2.5)
    );
  }

  /**
   * Decide online/offline from DATA RECENCY, independent of which transport is
   * carrying the data.
   *
   * THE BUG THIS REPLACES. checkDeviceTimeout() counted empty realtime POLL
   * responses. That worked only while polling was the delivery path. The moment
   * the SignalR socket came up, polling was suppressed by design — no responses,
   * no counting, and 'offline' became unreachable. Fixing the negotiate 401
   * therefore switched device-offline detection off, and the symptom was a grey
   * 'unknown' dot that looked enough like offline to be mistaken for it. Measured:
   * a ten-minute physical unplug produced ZERO network requests and no state
   * change at all.
   *
   * Recency has no such dependency. Both paths stamp lastDataReceivedTime — the
   * poll through setDeviceOnline(), and the live socket through the same call in
   * MainLayout.ingestFamily — so this reads the one fact that is true whichever
   * transport is live, including neither.
   *
   * @param now injectable for tests; never pass it from production code.
   */
  evaluateDeviceRecency(now: number = Date.now()): void {
    // Nothing has ever arrived for this project, so there is nothing to have
    // stopped arriving. That is 'unknown', and it is NOT offline.
    if (this.lastDataReceivedTime === 0) {
      return;
    }
    const silentFor = now - this.lastDataReceivedTime;
    const threshold = this.deviceOfflineThresholdMs();

    // CANNOT TELL. Too few arrivals to have measured a cadence, so there is no
    // honest threshold to compare against. Holding the current state is right:
    // asserting 'Offline' here would be a guess dressed as a finding, and that
    // guess would be wrong on exactly the projects whose MI is wrong.
    if (threshold === null) {
      return;
    }

    if (silentFor >= threshold) {
      if (this.deviceStatusSource.value !== 'offline') {
        console.log(
          `Device offline: silent for ${(silentFor / 1000).toFixed(1)}s, ` +
            `threshold ${(threshold / 1000).toFixed(1)}s ` +
            `(cadence ${(this.realtimeFetchGapMs() / 1000).toFixed(1)}s)`
        );
        this.deviceStatusSource.next('offline');
      }
      return;
    }

    // Data is current again. setDeviceOnline() already handles the arrival edge;
    // this covers the case where a stale 'offline' outlives the data that
    // contradicts it.
    if (this.deviceStatusSource.value === 'offline') {
      this.deviceStatusSource.next('online');
    }
  }

  checkUserAuthorization(
    tenantId: string,
    objectId: string
  ): Observable<AuthorizationResult> {
    const options = {
      params: new HttpParams()
        .set('tenantId', tenantId)
        .set('objectId', objectId),
    };

    return this.http
      .get<VericureUser>(environment.apiUrl + 'CheckUserExists', options)
      .pipe(
        // Retry only what is worth retrying. A denial is an answer and must not
        // be retried; a failure to answer is not evidence about the user and
        // must not be treated as one.
        retryWhen((errors) =>
          errors.pipe(
            concatMap((err, i) => {
              const classified = classifyAuthorizationError(err);
              const attempt = i + 1;
              if (
                classified.kind === 'denied' ||
                attempt >= AUTH_RETRY_ATTEMPTS
              ) {
                return throwError(() => err);
              }
              console.warn(
                `Authorization check unavailable (status=${classified.status}); ` +
                  `retry ${attempt}/${AUTH_RETRY_ATTEMPTS - 1} in ` +
                  `${authRetryDelayMs(attempt)}ms`
              );
              return timer(authRetryDelayMs(attempt));
            })
          )
        ),
        map((user): AuthorizationResult =>
          user
            ? { kind: 'authorized', user }
            : // A 200 with an empty body IS the server saying no.
              { kind: 'denied', status: 200 }
        ),
        catchError((err) => of(classifyAuthorizationError(err)))
      );
  }

  // setIsMetricUnit(val: boolean) {
  //   this.isMetricSource.next(val);
  // }

  unit_Temp_Val(val: number) {
    if (this.isMetricUnit) {
      return val;
    } else {
      return (val * 9) / 5 + 32;
    }
  }
  unit_Temp_Suffix() {
    if (this.isMetricUnit) {
      return '&deg;C';
    } else {
      return '&deg;F';
    }
  }

  formatUnixDateToHumanStringHHMMSS_Custom(unixTimestamp: number) {
    var date;

    //console.log('unix:' + unixTimestamp);
    date = new Date(unixTimestamp);

    //console.log('Curing Date:' + date);
    const hours = date.getUTCHours();
    const minutes = '0' + date.getUTCMinutes();
    const seconds = '0' + date.getUTCSeconds();
    if (isNaN(hours)) {
      return '';
    }

    var formattedTime =
      hours + ':' + minutes.substr(-2) + ':' + seconds.substr(-2);
    //console.log(formattedTime);
    return formattedTime;
  }

  formatDurationInSecToHHMMSS_Custom(durationSec: number) {
    // A duration cannot be negative, but an AXIS TICK can: when every value is
    // zero the range is degenerate and SciChart lays out ticks slightly below
    // zero. JS keeps the sign through %, so -0.5 gave Math.floor(-0.5/60) = -1,
    // '0' + '-1' -> '-1', and -1 % 24 = -1 — the "-1:-1:-1" labels on an axis
    // whose real value is simply nothing yet.
    if (!Number.isFinite(durationSec) || durationSec <= 0) {
      return '0:00:00';
    }
    const hours = Math.floor(durationSec / 3600);

    var days = Math.floor(hours / 24);
    var rem_hours = hours % 24;

    const minutes = '0' + Math.floor((durationSec % 3600) / 60);
    const seconds = '0' + Math.floor((durationSec % 3600) % 60);

    if (isNaN(hours)) {
      return '';
    }

    var formattedTime =
      rem_hours + ':' + minutes.substr(-2) + ':' + seconds.substr(-2);
    if (days > 0) {
      return days + '.' + formattedTime;
    } else {
      return formattedTime;
    }
  }

  /**
   * Check if a project has a saved unit preference
   * @param projectName The project name
   * @returns true if preference exists, false otherwise
   */
  hasProjectUnitPreference(projectName: string): boolean {
    return this.projectUnitPreferences.has(projectName);
  }

  /**
   * Get the unit preference for a specific project
   * @param projectName The project name
   * @returns true for metric, false for imperial. Returns default (true) if not set.
   */
  getProjectUnitPreference(projectName: string): boolean {
    if (!projectName) {
      return this.preferencesService.effective$.value.measurementSystem !== 'imperial';
    }
    if (this.projectUnitPreferences.has(projectName)) {
      return this.projectUnitPreferences.get(projectName)!;
    }
    return this.preferencesService.effective$.value.measurementSystem !== 'imperial';
  }

  /**
   * Set the unit preference for a specific project
   * @param projectName The project name
   * @param isMetric true for metric, false for imperial
   */
  setProjectUnitPreference(projectName: string, isMetric: boolean): void {
    if (projectName) {
      this.projectUnitPreferences.set(projectName, isMetric);
      // Update the current unit if this is the active project
      if (this.getProject() === projectName) {
        this.isMetricUnit = isMetric;
        this.toUpload_isMetricUnit = isMetric; // Keep both properties in sync
      }
    }
  }

  /**
   * Check if a project has a saved language preference
   * @param projectName The project name
   * @returns true if preference exists, false otherwise
   */
  hasProjectLanguagePreference(projectName: string): boolean {
    return this.projectLanguagePreferences.has(projectName);
  }

  /**
   * Get the language preference for a specific project
   * @param projectName The project name
   * @returns Language string. Returns default ('English') if not set.
   */
  getProjectLanguagePreference(projectName: string): string {
    const userLangCode = this.preferencesService.effective$.value.language;
    const fallback = LANG_CODE_TO_LABEL[userLangCode] ?? 'English';
    if (!projectName) {
      return fallback;
    }
    return this.projectLanguagePreferences.get(projectName) ?? fallback;
  }

  /**
   * Set the language preference for a specific project
   * @param projectName The project name
   * @param language The language string
   */
  setProjectLanguagePreference(projectName: string, language: string): void {
    if (projectName) {
      this.projectLanguagePreferences.set(projectName, language);
      // Update the current language if this is the active project
      if (this.getProject() === projectName) {
        this.selectedLang = language;
      }
    }
  }
}
